import {
    Alert,
    Box,
    Button,
    Container,
    GlobalStyles,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import config from '../../../config'
import {
    BRAND_CORAL,
    BRAND_CORAL_WASH,
    COOLDOWN_KEY,
    COOLDOWN_MS,
    FIELDS_BY_STEP,
    RECAPTCHA_ACTION,
    STEPS,
    defaultValues,
} from '../constants'
import useDraftPersistence, {
    clearDraft,
    readDraft,
    relativeTime,
} from '../hooks/useDraftPersistence'
import useRecaptcha from '../hooks/useRecaptcha'
import { StageKey, VendorOnboardingForm } from '../interfaces'
import { submitVendorOnboarding } from '../services/api'
import HoneypotField from './HoneypotField'
import SubmittedScreen from './SubmittedScreen'
import { NavButtons } from './StepChrome'
import AgreementStep from './steps/AgreementStep'
import CompanyStep from './steps/CompanyStep'
import ContactsStep from './steps/ContactsStep'
import InvoicingStep from './steps/InvoicingStep'
import ReviewStep from './steps/ReviewStep'

/**
 * Horizontal progress stepper. Completed steps (and the current one) are real
 * buttons, so a vendor can jump back to correct something without losing their
 * place; steps ahead stay inert because they haven't been validated yet.
 */
const StepRail = ({
    activeIndex,
    onJump,
}: {
    activeIndex: number
    onJump: (index: number) => void
}) => (
    <Box
        sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            width: '100%',
        }}
    >
        {STEPS.map((step, index) => {
            const isActive = index === activeIndex
            const isDone = index < activeIndex
            const canJump = index <= activeIndex
            const isLast = index === STEPS.length - 1

            return (
                <Fragment key={step.key}>
                    <Box
                        component="button"
                        type="button"
                        onClick={() => canJump && onJump(index)}
                        disabled={!canJump}
                        aria-current={isActive ? 'step' : undefined}
                        aria-label={`Step ${index + 1}: ${step.label}${
                            isDone ? ' (completed)' : ''
                        }`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            border: 0,
                            background: 'none',
                            p: 0,
                            font: 'inherit',
                            cursor: canJump ? 'pointer' : 'default',
                            flexShrink: 0,
                            '&:hover .step-dot': canJump
                                ? { transform: 'scale(1.08)' }
                                : {},
                        }}
                    >
                        <Box
                            className="step-dot"
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: 15,
                                fontWeight: 700,
                                transition: 'transform .15s, background-color .2s',
                                border: '2px solid',
                                ...(isDone
                                    ? {
                                          backgroundColor: BRAND_CORAL,
                                          borderColor: BRAND_CORAL,
                                          color: '#fff',
                                      }
                                    : isActive
                                      ? {
                                            backgroundColor: '#fff',
                                            borderColor: BRAND_CORAL,
                                            color: BRAND_CORAL,
                                            boxShadow: `0 0 0 4px ${BRAND_CORAL_WASH}`,
                                        }
                                      : {
                                            backgroundColor: '#fff',
                                            borderColor: 'divider',
                                            color: 'text.disabled',
                                        }),
                            }}
                        >
                            {isDone ? (
                                <CheckIcon sx={{ fontSize: 16 }} />
                            ) : (
                                index + 1
                            )}
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isActive ? 700 : 600,
                                whiteSpace: 'nowrap',
                                color: isActive
                                    ? BRAND_CORAL
                                    : isDone
                                      ? 'text.primary'
                                      : 'text.disabled',
                            }}
                        >
                            {step.label}
                        </Typography>
                    </Box>

                    {!isLast && (
                        <Box
                            sx={{
                                flex: 1,
                                height: 2,
                                mx: 1.5,
                                borderRadius: 1,
                                backgroundColor: isDone
                                    ? BRAND_CORAL
                                    : 'divider',
                                transition: 'background-color .2s',
                            }}
                        />
                    )}
                </Fragment>
            )
        })}
    </Box>
)

const BrandFooter = () => (
    <Typography
        variant="body2"
        sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}
    >
        Powered by{' '}
        <Link
            href="https://casaretail.ai/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                color: BRAND_CORAL,
                fontWeight: 700,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
            }}
        >
            Casa Retail AI
        </Link>
    </Typography>
)

const FinanceOnboardingPage = () => {
    const settings = config.financeOnboarding
    const methods = useForm<VendorOnboardingForm>({
        defaultValues,
        mode: 'onBlur',
    })

    const [stageIndex, setStageIndex] = useState(0)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [result, setResult] = useState<{ referenceId: string; email: string } | null>(
        null
    )
    const [draftPrompt, setDraftPrompt] = useState<{ savedAt: number } | null>(null)
    const [draftRestored, setDraftRestored] = useState(false)

    const renderedAtRef = useRef(Date.now())
    const { execute: executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha(
        settings?.recaptchaSiteKey
    )
    const { savedAt, markClean } = useDraftPersistence(methods.watch, !result)

    const stage = STEPS[stageIndex]

    useEffect(() => {
        document.title = 'Tenant Onboarding · Casa Retail AI'
    }, [])

    useEffect(() => {
        const draft = readDraft()
        if (draft) setDraftPrompt({ savedAt: draft.savedAt })
    }, [])

    const restoreDraft = () => {
        const draft = readDraft()
        if (draft) {
            methods.reset({
                ...defaultValues,
                ...draft.values,
                gstCertificate: null,
                website: '',
            })
            setDraftRestored(true)
        }
        setDraftPrompt(null)
    }

    const discardDraft = () => {
        clearDraft()
        setDraftPrompt(null)
    }

    const goTo = (key: StageKey) => {
        const index = STEPS.findIndex((s) => s.key === key)
        if (index >= 0) setStageIndex(index)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const next = async () => {
        const fields = FIELDS_BY_STEP[stage.key]
        const valid = await methods.trigger(fields as never, { shouldFocus: true })
        if (!valid) {
            toast.error('Please fix the highlighted fields before continuing.')
            return
        }
        setStageIndex((i) => Math.min(i + 1, STEPS.length - 1))
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const back = () => {
        setStageIndex((i) => Math.max(i - 1, 0))
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const submit = async () => {
        setSubmitError(null)

        const valid = await methods.trigger()
        if (!valid) {
            toast.error('Some details are still missing — please review the earlier steps.')
            return
        }

        const last = Number(window.localStorage.getItem(COOLDOWN_KEY) ?? 0)
        if (Date.now() - last < COOLDOWN_MS) {
            setSubmitError(
                'You just submitted this form. Please wait a moment before trying again.'
            )
            return
        }

        setIsSubmitting(true)
        try {
            const values = methods.getValues()
            const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION)

            const { referenceId } = await submitVendorOnboarding({
                values,
                recaptchaToken,
                renderedAt: renderedAtRef.current,
            })

            window.localStorage.setItem(COOLDOWN_KEY, String(Date.now()))
            clearDraft()
            markClean()
            setResult({ referenceId, email: values.submitterEmail })
        } catch (error) {
            const message =
                (error as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message ??
                (error as Error).message ??
                'Something went wrong.'
            setSubmitError(
                `We couldn't submit your details: ${message}. Your answers and attachment are still here — please try again.`
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    const progress = useMemo(
        () => ((stageIndex + 1) / STEPS.length) * 100,
        [stageIndex]
    )

    if (!settings?.webhookUrl) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Alert severity="error">
                    This form isn't configured for the current environment. Please contact
                    the Casa Retail AI finance team.
                </Alert>
            </Container>
        )
    }

    if (result) {
        return (
            <Box sx={{ backgroundColor: '#f7f8fa', minHeight: '100vh', py: 6 }}>
                <Container maxWidth="sm">
                    <SubmittedScreen
                        referenceId={result.referenceId}
                        submitterEmail={result.email}
                    />
                    <BrandFooter />
                </Container>
            </Box>
        )
    }

    return (
        <Box sx={{ backgroundColor: '#f7f8fa', minHeight: '100vh', pb: 8 }}>
            {/* Google permits hiding the badge as long as the attribution below is shown. */}
            <GlobalStyles styles={{ '.grecaptcha-badge': { visibility: 'hidden' } }} />
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Container maxWidth="md" sx={{ py: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1 }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 800, color: BRAND_CORAL }}
                        >
                            Tenant Finance Details
                        </Typography>
                        {savedAt && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Draft saved · {relativeTime(savedAt)}
                            </Typography>
                        )}
                    </Stack>

                    <StepRail activeIndex={stageIndex} onJump={setStageIndex} />

                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Step {stageIndex + 1} of {STEPS.length} · {stage.label}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                mt: 0.5,
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: BRAND_CORAL_WASH,
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: BRAND_CORAL,
                                },
                            }}
                        />
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ pt: 4 }}>
                {draftPrompt && (
                    <Alert
                        severity="info"
                        sx={{ mb: 3 }}
                        action={
                            <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={restoreDraft}>
                                    Resume
                                </Button>
                                <Button size="small" onClick={discardDraft}>
                                    Start fresh
                                </Button>
                            </Stack>
                        }
                    >
                        We found a saved draft from {relativeTime(draftPrompt.savedAt)}.
                    </Alert>
                )}

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 4 },
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                    }}
                >
                    <FormProvider {...methods}>
                        <form onSubmit={(event) => event.preventDefault()} noValidate>
                            <HoneypotField />

                            {stage.key === 'company' && <CompanyStep />}
                            {stage.key === 'agreement' && (
                                <AgreementStep needsReattach={draftRestored} />
                            )}
                            {stage.key === 'invoicing' && <InvoicingStep />}
                            {stage.key === 'contacts' && <ContactsStep />}
                            {stage.key === 'review' && (
                                <ReviewStep goTo={goTo} submitError={submitError} />
                            )}

                            <NavButtons
                                onBack={stageIndex > 0 ? back : undefined}
                                onNext={stage.key === 'review' ? submit : next}
                                nextLabel={
                                    stage.key === 'review' ? 'Submit details' : 'Continue'
                                }
                                isSubmitting={isSubmitting}
                            />
                        </form>
                    </FormProvider>
                </Paper>

                {recaptchaEnabled && (
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', textAlign: 'center', mt: 3, color: 'text.secondary' }}
                    >
                        Protected by reCAPTCHA ·{' '}
                        <a
                            href="https://policies.google.com/privacy"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Privacy
                        </a>{' '}
                        ·{' '}
                        <a
                            href="https://policies.google.com/terms"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Terms
                        </a>
                    </Typography>
                )}

                <BrandFooter />
            </Container>
        </Box>
    )
}

export default FinanceOnboardingPage
